import React, { useMemo, useState } from "react";
import { KebabButton } from "../../../../../components/KebabButton";
import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import type { DropdownAction } from "../../../../../components/Dropdown/types";
import { DateFormat, formatDate, formatCurrency } from "../../../../../utils";
import {
  isPricedValuation,
  valuationAmountText,
  valuationMessageFor,
} from "../../../../../services/valuation";
import type { AccountHeroProps } from "./types";
import { AccountActionSheet } from "./AccountActionSheet";
import {
  HeroCard,
  HeroGrid,
  LeftColumn,
  IdentityRow,
  HeroAccountName,
  HeroStatusBadge,
  HeroSubtitle,
  BalanceBlock,
  BalanceLabel,
  BalanceAmount,
  ChangeRow,
  ChangeChip,
  ChangeVsLabel,
  RightColumn,
  ActionsRow,
  StatsPanel,
  StatsList,
  StatItem,
  StatLabelBlock,
  StatLabel,
  StatSubLabel,
  StatValue,
  KebabWrapper,
} from "./styles";

export const AccountHero: React.FC<AccountHeroProps> = ({
  account,
  isDeleting,
  isUpdatingStatus,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Same rule as the account's card: a quote still in flight is a dash and a
  // "Pending"-shaped absence, never the flat assertion "Unavailable". The
  // leading nullness test is redundant with the helper and kept only because it
  // is what narrows `currentValue` for the formatting below.
  const hasCurrentValue =
    account.currentValue !== null &&
    isPricedValuation({
      value: account.currentValue,
      valuation: account.valuation,
    });
  const balanceText = valuationAmountText({
    value: account.currentValue,
    valuation: account.valuation,
  });
  const isNegativeBalance =
    account.currentValue !== null && account.currentValue < 0;
  const monthChange = hasCurrentValue ? account.monthChange : null;
  // The same message the account's card shows, so the list and this detail page cannot describe the
  // same missing valuation differently.
  const balanceLabel = hasCurrentValue
    ? "Current Balance"
    : valuationMessageFor(account.valuation);

  const monthChangeDisplay = useMemo(() => {
    if (monthChange === null) {
      return null;
    }
    const isNegative = monthChange < 0;
    const arrow = isNegative ? "↓" : "↑";
    const sign = isNegative ? "-" : "+";
    const amount = formatCurrency(Math.abs(monthChange));
    const percent = Math.abs(account.monthChangePercent ?? 0).toFixed(1);
    return {
      isNegative,
      text: `${arrow} ${sign}${amount} (${sign}${percent}%)`,
    };
  }, [monthChange, account.monthChangePercent]);

  const subtitle = account.institution
    ? `${account.institution} · Opened ${formatDate(account.startDate, DateFormat.DMY_SPACE)}`
    : `Opened ${formatDate(account.startDate, DateFormat.DMY_SPACE)}`;

  const { inAmount, outAmount, transactionsCount } = account;
  const avgTransaction = useMemo(() => {
    if (inAmount === null || outAmount === null || transactionsCount === 0) {
      return "—";
    }
    return formatCurrency((inAmount + outAmount) / transactionsCount);
  }, [inAmount, outAmount, transactionsCount]);

  const statusActionLabel = account.isActive ? "Deactivate" : "Reactivate";

  const dropdownActions = useMemo<DropdownAction[]>(
    () => [
      {
        id: "edit",
        label: "Edit",
        icon: "",
        onClick: () => onEdit(account),
        show: true,
      },
      {
        id: "status",
        label: statusActionLabel,
        icon: "",
        onClick: onStatusChange,
        disabled: isUpdatingStatus,
        show: true,
      },
      {
        id: "delete",
        label: "Delete",
        icon: "",
        onClick: onDelete,
        variant: "destructive" as const,
        disabled: isDeleting,
        show: true,
      },
    ],
    [
      account,
      statusActionLabel,
      isUpdatingStatus,
      isDeleting,
      onEdit,
      onStatusChange,
      onDelete,
    ],
  );

  return (
    <HeroCard elevation={0}>
      <KebabWrapper>
        <KebabButton
          aria-label="Account actions"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        />
      </KebabWrapper>
      <AccountActionSheet
        account={account}
        isOpen={menuOpen}
        isDeleting={isDeleting}
        isUpdatingStatus={isUpdatingStatus}
        onClose={() => setMenuOpen(false)}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
      />
      <HeroGrid>
        <LeftColumn>
          <IdentityRow>
            <HeroAccountName>{account.name}</HeroAccountName>
            <HeroStatusBadge $isActive={account.isActive}>
              {account.isActive ? "Active" : "Inactive"}
            </HeroStatusBadge>
          </IdentityRow>
          <HeroSubtitle>{subtitle}</HeroSubtitle>
          <BalanceBlock>
            <BalanceLabel>{balanceLabel}</BalanceLabel>
            <BalanceAmount
              $negative={isNegativeBalance}
              data-testid="hero-balance"
            >
              {balanceText}
            </BalanceAmount>
            {monthChangeDisplay && (
              <ChangeRow>
                <ChangeChip
                  $negative={monthChangeDisplay.isNegative}
                  data-testid="hero-month-change"
                >
                  {monthChangeDisplay.text}
                </ChangeChip>
                <ChangeVsLabel>vs last month</ChangeVsLabel>
              </ChangeRow>
            )}
          </BalanceBlock>
        </LeftColumn>

        <RightColumn>
          <ActionsRow>
            <ActionsDropdown
              dropdownActions={dropdownActions}
              testId="account-hero-actions"
            />
          </ActionsRow>

          <StatsPanel>
            <StatsList>
              <StatItem>
                <StatLabelBlock>
                  <StatLabel>Total Deposits</StatLabel>
                  <StatSubLabel>all time</StatSubLabel>
                </StatLabelBlock>
                <StatValue
                  $color="var(--color-success)"
                  data-testid="hero-deposits"
                >
                  {inAmount === null ? "—" : formatCurrency(inAmount)}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabelBlock>
                  <StatLabel>Total Withdrawals</StatLabel>
                  <StatSubLabel>all time</StatSubLabel>
                </StatLabelBlock>
                <StatValue
                  $color="var(--color-error)"
                  data-testid="hero-withdrawals"
                >
                  {outAmount === null ? "—" : formatCurrency(outAmount)}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabelBlock>
                  <StatLabel>Avg Transaction</StatLabel>
                  <StatSubLabel data-testid="hero-tx-count">
                    {account.transactionsCount} transactions
                  </StatSubLabel>
                </StatLabelBlock>
                <StatValue data-testid="hero-avg-tx">
                  {avgTransaction}
                </StatValue>
              </StatItem>
            </StatsList>
          </StatsPanel>
        </RightColumn>
      </HeroGrid>
    </HeroCard>
  );
};
